import ApiServiceV2 from './api-v2';
import ApiService from './api';
import ApiProtocolError from '../errors/ApiProtocolError';

function getLocalToken(): string | null {
  return localStorage.getItem('botcast_mobile_token');
}

/** Base URL of the standalone livechat API (from env). */
function getApiUrl(): string {
  return import.meta.env.VITE_LIVECHAT_API_URL;
}

// Helper to process API responses consistently
function handleResponse(response: any): any {
  // fallback for older API versions
  if (typeof response.success === 'undefined') {
    return response;
  }

  if (!response.success) {
    throw new ApiProtocolError(response.error);
  }
  return response.data;
}

/** A livechat agent (human operator) within an organization. */
export interface Agent {
  /** Unique agent id */
  uuid: string;
  /** Display name of the agent */
  name: string;
  /** Login/contact email of the agent */
  email: string;
  /** URL of the agent's avatar image */
  profilePicture: string;
  /** Organization the agent belongs to */
  organizationUuid: string;
  /** Creation timestamp (ISO string) */
  createdAt: string;
  /** Last-update timestamp (ISO string) */
  updatedAt: string;
  /** UUIDs of the groups the agent is a member of */
  groups: string[];
  /** Per-agent hub UI settings blob */
  hubSettings: object;
}

/** A routing group agents can be assigned to. */
export interface Group {
  /** Unique group id */
  uuid: string;
  /** Display name of the group */
  name: string;
  /** Free-text description of the group */
  description: string;
  /** Channel this group handles */
  channelId: string;
}

/** A livechat organization (one per customer/bot pair). */
export interface Organization {
  /** Unique organization id */
  uuid: string;
  /** The bot this organization is linked to */
  uniqueBotId: string;
  /** Seat limit for agents in this organization */
  maxNumberAgents: string;
  /** Staging bot id of the organization */
  stagingBotId: string;
  /** Production bot id of the organization */
  productionBotId: string;
  /** Agents belonging to the organization */
  agents: Agent[];
  /** Groups belonging to the organization */
  groups: Group[];
}

const LivechatService = {

  /**
   * Fetch agent details along with the organization's staging and production bot IDs.
   * This endpoint is restricted to users with the ADMIN role.
   *
   * @returns Promise resolving to agent details with organization bot IDs or error object
   */
  async getAgentAdminDetails(): Promise<any> {
    const requestData = {
      method: 'get',
      url: `${getApiUrl()}agent/admin-details`,
    };
    const response = await ApiServiceV2.customRequest(requestData);
    return handleResponse(response);
  },

  async getAgentByToken(): Promise<any> {
    const requestData = {
      method: 'get',
      url: `${getApiUrl()}agent/by-token`,
      data: {
        token: getLocalToken(),
      },
    };
    const response = await ApiServiceV2.customRequest(requestData);
    return handleResponse(response);
  },

  // probably not used
  async getAgents(organizationUuid: string): Promise<any> {
    const response = await ApiServiceV2.get(
      `${getApiUrl()}organization/${organizationUuid}/agents`
    );
    return handleResponse(response);
  },
  // probably not used
  async addAgent(agentUuid: string, organizationUuid: string): Promise<any> {
    const data = { agentUuid };
    const response = await ApiServiceV2.post(
      `${getApiUrl()}organization/${organizationUuid}/addagent`, data
    );
    return handleResponse(response);
  },

  /**
   * Removes an agent from a group
   * @returns The API response data
   * @throws ApiProtocolError When the API returns an error
   */
  async removeAgentGroup(agentUuid: string, groupUuid: string): Promise<any> {
    const data = { groups: [groupUuid] };
    const response = await ApiServiceV2.delete(
      `${getApiUrl()}agent/${agentUuid}/groups`, {
        data,
      }
    );
    return handleResponse(response);
  },

  /**
   * Adds agent to groups
   * @returns The API response data
   * @throws ApiProtocolError When the API returns an error
   */
  async addAgentGroups(agentUuid: string, groupUuids: string[]): Promise<any> {
    const data = { groups: groupUuids };
    const response = await ApiServiceV2.post(
      `${getApiUrl()}agent/${agentUuid}/groups`, data
    );
    return handleResponse(response);
  },

  /** @returns Whether a livechat organization exists for the bot; `undefined` on request error */
  async getBotHasLivechat(uniqueBotId: string): Promise<any> {
    try {
      const response = await ApiServiceV2.get(
        `${getApiUrl()}organization/exists?uniqueBotId=${uniqueBotId}`
      );
      return handleResponse(response);
    } catch (error) {
      // Keeping this try/catch for the console.error
      console.error('error', error);
    }
  },

  /**
   * Updates an agent; only the whitelisted profile fields are sent.
   * @param agent Agent fields; `isAgentSelf` switches to the self-service edit-profile endpoint
   */
  async updateAgent(agent: {
    /** Agent to update */
    uuid: string;
    /** New display name */
    name?: string;
    /** New avatar URL */
    profilePicture?: string;
    /** Per-agent hub UI settings blob */
    hubSettings?: any;
    /** Job title shown to visitors */
    title?: string;
    /** Languages the agent speaks */
    languages?: string[];
    /** Maximum simultaneously open chats for the agent */
    maxNumberOpenChats?: number;
    /** True when the agent edits their own profile (uses the edit-profile endpoint) */
    isAgentSelf?: boolean;
  }): Promise<any> {
    const { uuid, name, profilePicture, hubSettings, title, languages, maxNumberOpenChats, isAgentSelf = false } = agent;
    const changeset = Object.assign({}, { name, profilePicture, hubSettings, title, languages, maxNumberOpenChats });

    const requestData = {
      method: 'put',
      url: `${getApiUrl()}agent/${uuid}${isAgentSelf ? '/edit-profile' : ''}`,
      data: { changeset },
    };
    const response = await ApiServiceV2.customRequest(requestData);
    return handleResponse(response);
  },

  /** Creates an agent for the currently logged-in hub user (identified via the local token). */
  async createAgent(): Promise<any> {
    const res = await ApiServiceV2.post(`${getApiUrl()}agent`, {
      token: getLocalToken(),
    });
    return handleResponse(res);
  },

  /**
   * Creates an agent account for the email and adds it to the organization/groups in one call.
   */
  async createAndAddAgent(
    email: string,
    organizationUuid: string,
    groups: string[]
  ): Promise<any> {
    const res = await ApiServiceV2.post(`${getApiUrl()}agent/create-and-add`, {
      name: email,
      email,
      organizationUuid,
      groups,
    });
    return handleResponse(res);
  },

  async deleteAgent(agentUuid: string): Promise<any> {
    const res = await ApiServiceV2.delete(`${getApiUrl()}agent/${agentUuid}`);
    return handleResponse(res);
  },

  /** Lists hub users of the bot that are not livechat agents yet. */
  async getAllNonAgentUsers(uniqueBotId: string): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}management/non-agent-users`,
      params: { botId: uniqueBotId },
    });
    return handleResponse(response);
  },

  async createOrganization({
    uniqueBotId,
  }: {
    /** Bot the new organization is linked to */
    uniqueBotId: string;
  }): Promise<any> {
    const res = await ApiServiceV2.post(`${getApiUrl()}organization`, {
      uniqueBotId,
      maxNumberAgents: 10,
    });
    return handleResponse(res);
  },
  /**
   * @param group New group to create (uuid assigned by the backend)
   * @returns `{error: string}` or `{data: object}`
   */
  async addGroupToOrganization(
    organizationUuid: string,
    group: Partial<Group>
  ): Promise<any> {
    const res = await ApiServiceV2.post(
      `${getApiUrl()}organization/${organizationUuid}/group`,
      group
    );
    return handleResponse(res);
  },

  /**
   * @param group Group to update; `uuid` selects which group
   * @returns `{error: string}` or `{data: object}`
   */
  async updateGroup(organizationUuid: string, group: Group): Promise<any> {
    const res = await ApiServiceV2.post(
      `${getApiUrl()}organization/${organizationUuid}/group/${group.uuid}`,
      group
    );
    return handleResponse(res);
  },

  /**
   * Deletes a group from the organization.
   *
   * @param group Group to delete; only `uuid` is used
   * @returns `{error: string}` or `{data: object}`
   */
  async deleteGroup(organizationUuid: string, group: Group): Promise<any> {
    const res = await ApiServiceV2.delete(`${getApiUrl()}organization/${organizationUuid}/group/${group.uuid}`);

    if (!res) {
      throw new Error('Failed to delete group.');
    }
    return handleResponse(res);
  },

  /**
   * Safely updates non-critical organization data.
   *
   * @param organizationData Only `overbookingCoefficient` and `timeoutLimit` are forwarded
   * @returns The updated `{uuid, overbookingCoefficient, timeoutLimit}` values
   */
  async updateOrganizationLimited(
    organizationUuid: string,
    organizationData: {
      /** Whether agents may upload files directly in a livechat conversation */
      agentsCanUploadFiles?: boolean;
      /** Multiplier allowing agents to be assigned more chats than their limit */
      overbookingCoefficient?: number;
      /** Inactivity timeout for chats, in the backend's unit */
      timeoutLimit?: number;
      /** Other fields are ignored by this call */
      [key: string]: any;
    }
  ): Promise<any> {
    const { agentsCanUploadFiles, overbookingCoefficient, timeoutLimit } = organizationData;
    const res = await ApiServiceV2.patch(
      `${getApiUrl()}organization/${organizationUuid}`,
      {
        agentsCanUploadFiles,
        overbookingCoefficient,
        timeoutLimit,
      }
    );

    const response = handleResponse(res);
    return {
      uuid: organizationUuid,
      agentsCanUploadFiles: response.agentsCanUploadFiles,
      overbookingCoefficient: response.overbookingCoefficient,
      timeoutLimit: response.timeoutLimit,
    };
  },

  /** Admin-only variant that may also change `maxNumberAgents`. */
  async updateOrganizationAdmin(
    organizationUuid: string,
    organizationData: any
  ): Promise<any> {
    const res = await ApiServiceV2.patch(
      `${getApiUrl()}organization/${organizationUuid}/admin`,
      organizationData
    );

    const response = handleResponse(res);

    return {
      uuid: organizationUuid,
      overbookingCoefficient: response.overbookingCoefficient,
      maxNumberAgents: response.maxNumberAgents,
      timeoutLimit: response.timeoutLimit,
    };
  },

  async getChatByUuid({ chatUuid }: { chatUuid: string }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}chat/${chatUuid}`,
    });
    return handleResponse(response);
  },

  async getOpenChats({ agentUuid }: { agentUuid: string }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}agent/${agentUuid}/chats/open`,
    });
    return handleResponse(response);
  },

  async getUnassignedChats({
    uniqueBotId,
  }: {
    uniqueBotId: string;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}chat/unassigned`,
      params: {
        uniqueBotId,
      },
    });
    return handleResponse(response);
  },

  async getClosedChats({
    agentUuid,
    uniqueBotId,
  }: {
    agentUuid: string;
    uniqueBotId: string;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}agent/${agentUuid}/chats/closed`,
      params: {
        uniqueBotId,
        limit: 50,
      },
    });
    return handleResponse(response);
  },

  async getAllChats({
    agentUuid,
    uniqueBotId,
  }: {
    agentUuid: string;
    uniqueBotId: string;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}agent/${agentUuid}/chats/all`,
      params: {
        uniqueBotId,
        limit: 50,
      },
    });
    return handleResponse(response);
  },

  /**
   * @param status Chat status filter ('open'/'closed'/…); defaults to 'all'
   * @param options `limit`: maximum number of chats to return
   */
  async getAllChatsOrganization(
    {
      organizationUuid,
      status,
    }: {
      organizationUuid: string;
      status?: string | null;
    },
    { limit }: { limit?: number | string } = { limit: 10 }
  ): Promise<any> {
    const type = status || 'all'; // Chat statuses

    const res = await ApiServiceV2.customRequest({
      method: 'get',
      url: `${getApiUrl()}organization/${organizationUuid}/chats/${type}`,
      params: {
        limit: Number(limit) || 10,
      },
    });
    const response = handleResponse(res);
    // This endpoint answers with an extra `{ data: ... }` envelope that `handleResponse` only
    // strips when the body carries a `success` flag, so unwrap it here when it is still present.
    return response?.data ?? response;
  },

  async assignChat({
    chatUuid,
    agentUuid,
    uniqueUserId,
  }: {
    /** Chat to assign */
    chatUuid: string;
    /** Agent that takes the chat */
    agentUuid: string;
    /** End user (visitor) the chat belongs to */
    uniqueUserId: string;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'post',
      url: `${getApiUrl()}chat/${chatUuid}/assign`,
      data: {
        agentUuid,
        chatUuid,
        uniqueUserId,
      },
    });
    return handleResponse(response);
  },

  async reassignChat({
    chatUuid,
    agentUuid,
  }: {
    chatUuid: string;
    agentUuid: string;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'post',
      url: `${getApiUrl()}chat/${chatUuid}/reassign`,
      data: { agentUuid },
    });
    return handleResponse(response);
  },

  async reassignChatToGroup({
    chatUuid,
    groupUuid,
  }: {
    chatUuid: string;
    groupUuid: string;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'post',
      url: `${getApiUrl()}chat/${chatUuid}/assign-to-group`,
      data: { groups: [groupUuid] },
    });
    return handleResponse(response);
  },

  //TODO: Remove uniqueUserId and isStaging as they are not used in the API
  async closeChat({
    chatUuid,
    uniqueUserId,
    isStaging,
  }: {
    chatUuid: string;
    /** Unused by the API — kept for caller compatibility (see TODO) */
    uniqueUserId?: string;
    /** Unused by the API — kept for caller compatibility (see TODO) */
    isStaging?: boolean;
  }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'put',
      url: `${getApiUrl()}chat/${chatUuid}/status`,
      data: {
        status: 'closed',
      },
    });
    return handleResponse(response);
  },

  async closeChatSupervisor({ chatUuid }: { chatUuid: string }): Promise<any> {
    const response = await ApiServiceV2.customRequest({
      method: 'put',
      url: `${getApiUrl()}chat/${chatUuid}/close-supervisor`,
    });
    return handleResponse(response);
  },

  async getChatArchive({
    botId,
    uniqueUserId,
  }: {
    botId: string;
    /** Visitor whose chat history is fetched */
    uniqueUserId: string;
  }): Promise<any> {
    try {
      const response = await ApiService.get(
        `/schaltzentrale/chats/history/${botId}/${encodeURIComponent(uniqueUserId)}/archive`
      );
      // `ApiService` resolves to the raw AxiosResponse and this endpoint's body carries no
      // `success` flag, so `handleResponse` hands it straight back — the payload is on `.data`.
      return handleResponse(response).data;
    } catch (error) {
      return { error };
    }
  },

  async sendEmailArchive({
    uniqueBotId,
    uniqueUserId,
  }: {
    uniqueBotId: string;
    /** Visitor whose chat archive is emailed */
    uniqueUserId: string;
  }): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/notification/archive`, { uniqueBotId, uniqueUserId });
      // Same envelope as `getChatArchive`: unwrap the AxiosResponse so callers see the body's
      // `{ success, message, error }` instead of axios' `{ data, status, headers, … }`.
      return handleResponse(response).data;
    } catch (error) {
      return { error };
    }
  },

  /** Writes the `moin_livechat` bot settings (webhook + default group) when enabling livechat. */
  async addBotSettings({
    botId,
    defaultGroup,
  }: {
    botId: string;
    /** Group new takeovers are routed to by default */
    defaultGroup: Group;
  }): Promise<any> {
    const response = await ApiService.post(
      `/schaltzentrale/bot/settings/${botId}/moin_livechat`,
      {
        webhook: `${import.meta.env.VITE_BOT_URL}/incoming/moin-livechat`,
        initMessage:
          'Dies ist ein Chatbot Takeover. Conversation_id (ignore): {ctxval:moin_livechat_conversation_id} Bisherige Unterhaltung öffnen: {ctxval:link}',
        groups: [defaultGroup.uuid],
      }
    );
    return handleResponse(response);
  },

  async getOrganizatioByBotId(uniqueBotId: string): Promise<any> {
    try {
      const response = await ApiServiceV2.get(
        `${getApiUrl()}organization/?uniqueBotId=${uniqueBotId}`
      );
      return handleResponse(response);
    } catch (error) {
      // Keeping this try/catch for the console.error
      console.error('error', error);
      throw error;
    }
  },
  /**
   * @param uuid Organization to fetch
   * @returns `{error: string}` or `{data: object | null}`
   */
  async getOrganizatioByUuid(uuid: string): Promise<any> {
    const response = await ApiServiceV2.get(
      `${getApiUrl()}organization/${uuid}`
    );
    return handleResponse(response);
  },

  /**
   * @param organizationUuid Organization whose chat stats are fetched
   * @param start The start date.
   * @param end The end date.
   * @param botId The bot ID.
   * @param page The page number for pagination.
   * @param limit The number of items per page.
   * @param search Search term to filter results.
   * @param tags Tags to filter results.
   * @param sortBy The field to sort by.
   * @param sortOrder The order of sorting (true for 'asc', false for 'desc').
   */
  async getStatsForOrganization(
    organizationUuid: string,
    start: string | null,
    end: string | null,
    botId: string | null,
    page: number = 1,
    limit: number = 100,
    search?: string | null,
    tags: string[] = [],
    sortBy: string | null = null,
    sortOrder: boolean = true
  ): Promise<any> {
    const botIdQuery = botId ? `&botId=${botId}` : '';
    const pageQuery = `&page=${page}&limit=${limit}`;
    const searchQuery = search ? `&search=${encodeURIComponent(search)}` : '';
    const tagsQuery = tags.length > 0 ? `&tags=${encodeURIComponent(tags.join(','))}` : '';
    const sortByQuery = sortBy ? `&sortBy=${sortBy}&sortOrder=${sortOrder}` : '';

    const response = await ApiServiceV2.get(
      `${getApiUrl()}organization/${organizationUuid}/stats/chats?start=${start}&end=${end}${botIdQuery}${pageQuery}${searchQuery}${tagsQuery}${sortByQuery}`
    );
    return handleResponse(response);
  },

  async addNoteToChat(chatUuid: string, note: string): Promise<any> {
    const response = await ApiServiceV2.post(
      `${getApiUrl()}chat/${chatUuid}/agent-note`,
      { note }
    );
    return handleResponse(response);
  },

  async removeNoteFromChat(chatUuid: string, noteId: string): Promise<any> {
    const response = await ApiServiceV2.delete(
      `${getApiUrl()}chat/${chatUuid}/agent-note/${noteId}`
    );
    return handleResponse(response);
  },
  /** Checks whether any agent of the given groups is available to take a chat. */
  async isAvailable({
    groups,
    uniqueBotId,
  }: {
    /** Group UUIDs to check availability in */
    groups: string[];
    uniqueBotId: string;
  }): Promise<any> {
    const requestData = {
      method: 'post',
      url: `${getApiUrl()}agent/available-agents`,
      data: { groups, uniqueBotId }
    };
    const response = await ApiServiceV2.customRequest(requestData);
    return handleResponse(response);
  },
  async getOnlineAgents(organizationUuid: string): Promise<any> {
    const requestData = {
      method: 'get',
      url: `${getApiUrl()}organization/${organizationUuid}/agents/online`
    };
    const response = await ApiServiceV2.customRequest(requestData);
    return handleResponse(response);
  },
  async addFileToOrganization(
    organizationUuid: string,
    file: {
      /** Display name of the shared file */
      name: string;
      /** Download URL of the shared file */
      url: string;
      /** Description of the shared file */
      description?: string;
      /** File extension (without the leading dot) */
      extension?: string;
      /** MIME type of the file */
      mimeType?: string;
      /** File size in bytes */
      size?: number | null;
      /** Original filename as uploaded (before uniqueness disambiguation) */
      originalFilename?: string;
    }
  ): Promise<any> {
    const response = await ApiServiceV2.post(`${getApiUrl()}organization/${organizationUuid}/file`, file);
    return handleResponse(response);
  },
  async getOrganizationTags(organizationUuid: string): Promise<any> {
    const response = await ApiServiceV2.get(`${getApiUrl()}organization/${organizationUuid}/tags`);
    return handleResponse(response);
  },

  async removeFileFromOrganization(
    organizationUuid: string,
    fileName: string
  ): Promise<any> {
    const response = await ApiServiceV2.delete(`${getApiUrl()}organization/${organizationUuid}/file/${fileName}`);
    return handleResponse(response);
  },

  /** @param tag Tag definition (name/color etc. — shape owned by the tag editor) */
  async addOrganizationTag(organizationUuid: string, tag: any): Promise<any> {
    const response = await ApiServiceV2.post(`${getApiUrl()}organization/${organizationUuid}/tag`, tag);
    return handleResponse(response);
  },

  async addShortcodeToOrganization(
    organizationUuid: string,
    shortcode: {
      /** The shortcode trigger, e.g. "hi" */
      code: string;
      /** The expanded text inserted into the chat */
      text: string;
    }
  ): Promise<any> {
    // shortcode: { code: string, text: string }
    const response = await ApiServiceV2.post(`${getApiUrl()}organization/${organizationUuid}/shortcode`, shortcode);
    return handleResponse(response);
  },
  /**
   * @param tagName Current name of the tag to update
   * @param tagData New tag definition
   */
  async updateOrganizationTag(
    organizationUuid: string,
    tagName: string,
    tagData: any
  ): Promise<any> {
    const response = await ApiServiceV2.put(`${getApiUrl()}organization/${organizationUuid}/tag/${tagName}`, tagData);
    return handleResponse(response);
  },
  async setSupervisorAgentOffline(supervisorAgentUuid: string): Promise<any> {
    const requestData = {
      method: 'put',
      url: `${getApiUrl()}agent/${supervisorAgentUuid}/status`,
      data: { status: 'offline' }
    };
    const response = await ApiServiceV2.customRequest(requestData);
    return handleResponse(response);
  },
  async updateShortcodeForOrganization(
    organizationUuid: string,
    code: string,
    shortcode: {
      /** The shortcode trigger, e.g. "hi" */
      code: string;
      /** The expanded text inserted into the chat */
      text: string;
    }
  ): Promise<any> {
    // shortcode: { code: string, text: string }
    const response = await ApiServiceV2.put(`${getApiUrl()}organization/${organizationUuid}/shortcode/${code}`, shortcode);
    return handleResponse(response);
  },

  async deleteOrganizationTag(
    organizationUuid: string,
    tagTitle: string
  ): Promise<any> {
    const response = await ApiServiceV2.delete(`${getApiUrl()}organization/${organizationUuid}/tag/${tagTitle}`);
    return handleResponse(response);
  },
  async removeShortcodeFromOrganization(
    organizationUuid: string,
    code: string
  ): Promise<any> {
    const response = await ApiServiceV2.delete(`${getApiUrl()}organization/${organizationUuid}/shortcode/${code}`);
    return handleResponse(response);
  },
  /**
   * Adds a tag to a chat.
   */
  async addTagToChat(chatUuid: string, tag: string): Promise<any> {
    if (!tag) {
      return { error: 'Tag parameter is required' };
    }
    const response = await ApiServiceV2.post(
      `${getApiUrl()}chat/${chatUuid}/tag`,
      { tag }
    );
    return handleResponse(response);
  },
  async updateFileDescription(
    organizationUuid: string,
    fileName: string,
    description: string
  ): Promise<any> {
    const response = await ApiServiceV2.patch(
      `${getApiUrl()}organization/${organizationUuid}/file/${fileName}/description`,
      { description }
    );
    return handleResponse(response);
  },
  /**
   * Removes a tag from a chat.
   */
  async removeTagFromChat(chatUuid: string, tag: string): Promise<any> {
    if (!tag) {
      return { error: 'Tag parameter is required' };
    }
    const response = await ApiServiceV2.delete(
      `${getApiUrl()}chat/${chatUuid}/tag`,
      { data: { tag } }
    );
    return handleResponse(response);
  }
};

export default LivechatService;
